import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { IoIosCloseCircle } from "react-icons/io";
import { cmsSegments } from "constants/cmsSegments";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: ${({ show }) => (show ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(2px);
  z-index: 1200;
  padding: 24px;
`;

const Modal = styled.div`
  background: #ffffff;
  width: min(640px, 95vw);
  border-radius: 16px;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.25);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #eceff3;
`;

const Title = styled.h3`
  margin: 0;
  font-weight: 700;
  letter-spacing: 0.2px;
`;

const CloseIcon = styled(IoIosCloseCircle)`
  font-size: 28px;
  cursor: pointer;
  color: #94a3b8;
  transition: color 0.2s ease;
  &:hover {
    color: #0f172a;
  }
`;

const Body = styled.div`
  padding: 20px 24px 26px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 14px;
  color: #0f172a;
`;

const Input = styled.input`
  height: 42px;
  border: 1px solid #d8dde4;
  border-radius: 10px;
  padding: 0 12px;
  font-size: 14px;
  transition: border 0.2s ease, box-shadow 0.2s ease;
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
  }
`;

const Textarea = styled.textarea`
  border: 1px solid #d8dde4;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  transition: border 0.2s ease, box-shadow 0.2s ease;
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const Button = styled.button`
  min-width: 120px;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 600;
  border: 1px solid ${({ variant }) => (variant === "ghost" ? "#e2e8f0" : "#2563eb")};
  background: ${({ variant }) => (variant === "ghost" ? "#ffffff" : "#2563eb")};
  color: ${({ variant }) => (variant === "ghost" ? "#0f172a" : "#ffffff")};
  cursor: pointer;
  box-shadow: ${({ variant }) =>
    variant === "ghost" ? "none" : "0 10px 30px rgba(37, 99, 235, 0.25)"};
  transition: transform 0.1s ease, box-shadow 0.1s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${({ variant }) =>
      variant === "ghost"
        ? "0 10px 30px rgba(15, 23, 42, 0.06)"
        : "0 12px 34px rgba(37, 99, 235, 0.3)"};
  }
`;

const Helper = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 13px;
`;

const SegmentsWrap = styled.div`
  border: 1px solid #d8dde4;
  border-radius: 12px;
  padding: 12px;
  background: #f8fafc;
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #0f172a;
`;

const EditorForm = ({ show, onClose, onSubmit, initialData }) => {
  const isEdit = Boolean(initialData);
  const [formData, setFormData] = useState({
    name: "",
    contactNumber: "",
    location: "",
    roleId: "",
    id: "",
    password: "",
    assignedSections: [],
    contactInfo: "",
  });

  const editorRoleOnlyId = "";

  const segmentOptions = useMemo(
    () => cmsSegments.filter((segment) => segment.assignable !== false).map((segment) => segment.title),
    []
  );

  useEffect(() => {
    if (isEdit && initialData) {
      setFormData({
        name: initialData.name || "",
        contactNumber: initialData.contactNumber || "",
        location: initialData.location || initialData.district || "",
        roleId: initialData.role?._id || editorRoleOnlyId,
        id: initialData.id || "",
        password: "",
        assignedSections: initialData.assignedSections || [],
        contactInfo: initialData.contactInfo || initialData.contactNumber || "",
      });
    } else {
      setFormData({
        name: "",
        contactNumber: "",
        location: "",
        roleId: editorRoleOnlyId,
        id: "",
        password: "",
        assignedSections: [],
        contactInfo: "",
      });
    }
  }, [isEdit, initialData, show, editorRoleOnlyId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      name: formData.name.trim(),
      contactNumber: formData.contactNumber.trim(),
      location: formData.location.trim(),
      district: formData.location.trim(), // keep legacy field until backend fully migrates
      roleId: formData.roleId || "",
      id: formData.id.trim(),
      assignedSections: formData.assignedSections,
      contactInfo: formData.contactInfo.trim(),
    };

    if (!payload.name || !payload.id) {
      alert("Name and Login ID are required.");
      return;
    }

    if (!isEdit || formData.password.trim()) {
      payload.password = formData.password.trim();
    }

    onSubmit(payload);
  };

  return (
    <Overlay show={show}>
      <Modal>
        <Header>
          <div>
            <Title>{isEdit ? "Edit Editor" : "Add Editor"}</Title>
            <Helper>Editors can log in with a unique ID; data stays even if the user is removed.</Helper>
          </div>
          <CloseIcon onClick={onClose} />
        </Header>

        <Body>
          <form onSubmit={handleSubmit}>
            <FormGrid>
              <Field>
                <Label>Name</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter full name"
                />
              </Field>

              <Field>
                <Label>Contact Number</Label>
                <Input
                  type="text"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  placeholder="e.g. +91 9876543210"
                />
              </Field>

              <Field>
                <Label>Location</Label>
                <Input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Enter location"
                />
              </Field>

              <Field>
                <Label>Role</Label>
                <Input type="text" value="Editor" disabled />
                <Helper>Only the Editor role is allowed here.</Helper>
              </Field>

              <Field>
                <Label>Login ID</Label>
                <Input
                  type="text"
                  name="id"
                  value={formData.id}
                  onChange={handleChange}
                  required
                  placeholder="e.g. editor@example.com"
                  disabled={isEdit}
                />
              </Field>

              <Field>
                <Label>{isEdit ? "Reset Password (optional)" : "Password"}</Label>
                <Input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!isEdit}
                  placeholder={isEdit ? "Leave blank to keep current password" : "Choose a strong password"}
                />
              </Field>
            </FormGrid>

            <Field>
              <Label>Assign Segments</Label>
              <SegmentsWrap>
                <CheckboxGrid>
                  {segmentOptions.map((segment) => (
                    <CheckboxLabel key={segment}>
                      <input
                        type="checkbox"
                        checked={formData.assignedSections.includes(segment)}
                        onChange={() =>
                          setFormData((prev) => {
                            const isSelected = prev.assignedSections.includes(segment);
                            return {
                              ...prev,
                              assignedSections: isSelected
                                ? prev.assignedSections.filter((s) => s !== segment)
                                : [...prev.assignedSections, segment],
                            };
                          })
                        }
                      />
                      {segment}
                    </CheckboxLabel>
                  ))}
                </CheckboxGrid>
              </SegmentsWrap>
              <Helper>Select which content areas this editor can manage.</Helper>
            </Field>

            <Footer>
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">{isEdit ? "Update Editor" : "Create Editor"}</Button>
            </Footer>
          </form>
        </Body>
      </Modal>
    </Overlay>
  );
};

export default EditorForm;
